import { useMemo, useState } from "react";

import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl-compat";

import { Checkbox } from "@ps-design-system/components/Checkbox";
import { Text } from "@ps-design-system/components/Text";

import HowDidYouHearModal, {
  isHowDidYouHeardOptionKey,
} from "../../../app-profile/HowDidYouHearModal";
import { useSelector } from "react-redux";
import { selectProfileHostHeardFrom } from "../../../app-main/selectors/profile.selectors";
import { Input } from "@ps-design-system/components/Input";
import {
  createHoseHearFromOptionsFromTranslations,
  getCurrentHostHearFromValues,
} from "./HowDidYouHearOptions.utils";

/**
 * Read-only summary of how the host found Peerspace. The checkboxes here are
 * display only — interacting with any of them opens `HowDidYouHearModal`, which
 * owns the form and the save. Nothing toggles in place, so `checked` always
 * reflects the persisted answers rather than local state.
 */
export const HowDidYouHearOptions = () => {
  const intl = useIntl();
  const [isEditOpen, setIsEditOpen] = useState(false);

  // The selector is `string[] | undefined` — a host who has never answered has
  // no `host_heard_from` at all. The annotation is needed because `useSelector`
  // is untyped in this app, so it hands back `any`.
  const hostHeardFrom: string[] = useSelector(selectProfileHostHeardFrom) ?? [];

  const currentValues = getCurrentHostHearFromValues(hostHeardFrom);

  // Shuffled once on mount so the order stays put across re-renders.
  const options = useMemo(
    () => createHoseHearFromOptionsFromTranslations(intl),
    [intl],
  );

  const openEditModal = () => setIsEditOpen(true);

  // `options` holds translated labels, so each one has to be narrowed back to a
  // known key before it can be looked up in the saved answers.
  const isOptionSelected = (option: string) =>
    isHowDidYouHeardOptionKey(option) && currentValues[option];

  return (
    <div className="col-xs-12 tw-mb-32">
      <hr className="tw-mb-36" />
      <Text typeface="headline-4">
        <FormattedMessage
          id="hosting.how_did_you_hear_options.heading"
          defaultMessage="How did you hear about hosting with Peerspace?"
        />
      </Text>
      <Text typeface="body-2" className="tw-mb-36">
        <FormattedMessage
          id="hosting.how_did_you_hear_options.select_text"
          defaultMessage="Select all that apply to help us find more hosts like you."
        />
      </Text>
      <br />
      <div className="tw-gap-16 tw-grid tw-grid-cols-2">
        {options.map((option, index) => (
          <Checkbox
            key={option}
            // `Checkbox` renders `description` in a `<Label htmlFor={id}>`, so
            // without an `id` the text is not tied to the box: clicking it does
            // nothing and the box has no accessible name. Index is enough here —
            // the id only has to be unique within the document, and slugging the
            // translated label would collapse to "" in non-latin locales.
            id={`how-did-you-hear-${index}`}
            checked={isOptionSelected(option)}
            onCheckedChange={openEditModal}
            // `tw-normal-case` undoes the legacy global `label
            // { text-transform: lowercase }` rule in styleguide.less,
            // which otherwise inherits into the checkbox description.
            description={<Text className="tw-normal-case">{option}</Text>}
            alignment="top"
          />
        ))}
        <span className="tw-col-span-1 tw-flex tw-flex-col">
          <Checkbox
            id="how-did-you-hear-other"
            checked={currentValues.Other}
            onCheckedChange={openEditModal}
            description={
              <Text className="tw-normal-case">
                <FormattedMessage
                  id="hosting.how_did_you_hear_options.checkbox_label_other"
                  defaultMessage="Other"
                />
              </Text>
            }
            alignment="top"
          />
          <div className="tw-mt-8">
            <Input
              name="otherDetail"
              // The "Other" checkbox labels this visually, but it is a separate
              // control, so it needs its own accessible name.
              aria-label={intl.formatMessage({
                id: "hosting.how_did_you_hear_options.other_input_label",
                defaultMessage:
                  "How else did you hear about hosting with Peerspace?",
              })}
              placeholder={intl.formatMessage({
                id: "hosting.how_did_you_hear_options.other_input_placeholder",
                defaultMessage: "Tell us more",
              })}
              value={currentValues.otherDetail}
              readOnly
              onClick={openEditModal}
            />
          </div>
        </span>
      </div>
      {isEditOpen && (
        <HowDidYouHearModal
          defaultValues={currentValues}
          open={isEditOpen}
          options={options}
          onOpenChange={setIsEditOpen}
        />
      )}
    </div>
  );
};
